import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, CornerDownLeft, Link2 } from 'lucide-react';
import { searchService } from '../services/api';

// Universal Enterprise Search UI Surface (Expansion Package I, Phase 2).
// Permission-aware, read-only, flag-gated by `universal_search_enabled`. Owns no
// data; uses the existing /api/search service and its extensible entity registry.
// Hidden entirely when the flag is OFF (no search requests are made).
const TYPE_LABELS = {
  lead: 'Leads', quote: 'Quotes', application: 'Applications', load: 'Loads',
  document: 'Documents', staff: 'Staff', user: 'Users',
  compliance_item: 'Compliance', settlement: 'Settlements', job_posting: 'Job Postings',
};

const groupByType = (results) => {
  const groups = {};
  (results || []).forEach((r) => {
    (groups[r.entity_type] = groups[r.entity_type] || []).push(r);
  });
  return groups;
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [available, setAvailable] = useState(null); // null=checking, false=flag off, true=on
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    searchService.isEnabled().then((v) => { if (mounted) setAvailable(v); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, []);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); setSearched(false); setError(null); return; }
    setLoading(true); setError(null);
    try {
      const data = await searchService.search(q.trim(), { includeContext: true, limit: 20 });
      setResults(data.results || []);
      setSearched(true);
    } catch (e) {
      if (e?.response?.status === 403) {
        // Flag turned off server-side — hide the surface.
        setAvailable(false); setOpen(false);
      } else {
        setError('Search is temporarily unavailable. Please try again.');
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 350);
  };

  const goTo = (r) => {
    setOpen(false); setQuery(''); setResults([]); setSearched(false);
    if (r.url) navigate(r.url);
  };

  if (available !== true) return null; // hidden when flag OFF or still checking

  const groups = groupByType(results);
  const hasResults = results.length > 0;

  return (
    <div className="relative" ref={boxRef} data-testid="global-search">
      {!open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
          data-testid="global-search-open"
          aria-label="Open enterprise search"
        >
          <Search className="w-4 h-4" />
          <span className="hidden md:inline">Search…</span>
          <span className="hidden lg:inline text-xs text-gray-400 border border-gray-200 rounded px-1 ml-1">⌘K</span>
        </button>
      ) : (
        <div className="fixed left-1/2 -translate-x-1/2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-0 sm:translate-x-0 z-50 w-[92vw] sm:w-[520px]">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 px-3 border-b border-gray-100">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={onChange}
                placeholder="Search across your authorized records…"
                className="flex-1 py-3 text-sm outline-none"
                data-testid="global-search-input"
                aria-label="Enterprise search input"
              />
              {loading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" data-testid="global-search-loading" />}
              <button onClick={() => { setOpen(false); }} className="text-gray-400 hover:text-gray-700" data-testid="global-search-close" aria-label="Close search">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
              {error && (
                <div className="p-6 text-center text-sm text-red-600" data-testid="global-search-error">
                  {error}
                  <div className="mt-2">
                    <button onClick={() => runSearch(query)} className="text-blue-600 hover:underline text-xs">Retry</button>
                  </div>
                </div>
              )}

              {!error && query.trim().length < 2 && (
                <div className="p-6 text-center text-sm text-gray-400" data-testid="global-search-hint">
                  Type at least 2 characters to search your authorized records.
                </div>
              )}

              {!error && !loading && searched && !hasResults && query.trim().length >= 2 && (
                <div className="p-6 text-center text-sm text-gray-500" data-testid="global-search-empty">
                  No results found for “{query.trim()}”.
                </div>
              )}

              {!error && hasResults && Object.keys(groups).map((type) => (
                <div key={type} className="py-1" data-testid={`global-search-group-${type}`}>
                  <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {TYPE_LABELS[type] || type}
                  </div>
                  {groups[type].map((r) => (
                    <button
                      key={`${r.entity_type}-${r.entity_id}`}
                      onClick={() => goTo(r)}
                      className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-start gap-3 group"
                      data-testid={`global-search-result-${r.entity_type}-${r.entity_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                        {r.snippet && <div className="text-xs text-gray-500 truncate">{r.snippet}</div>}
                        {r.context && r.context.relationship_count > 0 && (
                          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-indigo-600" data-testid="global-search-context">
                            <Link2 className="w-3 h-3" />
                            <span>{r.context.relationship_count} related · {Object.keys(r.context.related_types || {}).map((k) => TYPE_LABELS[k] || k).join(', ')} · ERS (reference-only)</span>
                          </div>
                        )}
                      </div>
                      <CornerDownLeft className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 mt-1 shrink-0" />
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400 flex items-center justify-between">
              <span>Permission-aware · shows only records you can access</span>
              <span>Esc to close</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
